import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getRepoStats } from '../api/repos.js';
import { useApi } from '../api/use-api.js';
import { GraphCanvas } from '../components/graph-canvas.js';
import { GraphLegend } from '../components/graph-legend.js';
import { GraphStatsOverlay } from '../components/graph-stats-overlay.js';
import { GraphStatusBar } from '../components/graph-status-bar.js';
import { LeftPanel } from '../components/left-panel.js';
import { NodeSourcePanel } from '../components/node-source-panel.js';
import { RightPanel } from '../components/right-panel.js';
import { useRepoGraph } from '../hooks/use-repo-graph.js';
import { useUiStore } from '../state/ui-store.js';

/**
 * `/repos/:name` — full-screen graph canvas (G.2).
 *
 * Owns the data fetch + Sigma lifecycle via `useRepoGraph`, then composes
 * the canvas + side drawers + status bar + stats overlay. Replaces the
 * pre-G.2 stat-card page; legacy stats now render inside the overlay.
 */
export function RepoDetail(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const slug = name ?? '';

  const setSelectedNodeId = useUiStore((s) => s.setSelectedNodeId);
  const selectedNodeId = useUiStore((s) => s.selectedNodeId);

  const { query, sigma, graphology } = useRepoGraph({
    slug,
    onNodeSelect: setSelectedNodeId,
  });

  const api = useApi();
  const statsQuery = useQuery({
    queryKey: ['repo-stats', slug],
    queryFn: () => getRepoStats(api, slug),
    enabled: Boolean(slug),
  });

  // Reset selection when navigating between repos.
  useEffect(() => {
    setSelectedNodeId(null);
  }, [slug, setSelectedNodeId]);

  const selectedLabel = useMemo(
    () => (selectedNodeId ? sigma.getNodeLabel(selectedNodeId) : null),
    [selectedNodeId, sigma],
  );

  if (!slug) return <p className="p-8 text-text-muted">Missing repo name.</p>;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <GraphCanvas
        containerRef={sigma.containerRef}
        loading={query.isLoading}
        error={query.isError ? query.error : null}
      >
        <GraphStatsOverlay
          slug={slug}
          stats={statsQuery.data}
          loading={statsQuery.isLoading}
          error={statsQuery.error}
        />
        <LeftPanel
          graph={graphology}
          selectedNodeId={selectedNodeId}
          onNodeSelect={setSelectedNodeId}
          onZoomTo={sigma.zoomTo}
        />
        <RightPanel />
        <GraphLegend />
        <NodeSourcePanel
          selectedNodeId={selectedNodeId}
          graph={graphology}
          slug={slug}
          onClose={() => setSelectedNodeId(null)}
          onNodeSelect={(id) => {
            setSelectedNodeId(id);
            sigma.zoomTo(id);
          }}
        />
      </GraphCanvas>
      <GraphStatusBar
        nodeCount={query.data?.totalNodes}
        edgeCount={query.data?.totalEdges}
        truncated={query.data?.truncated ?? false}
        layoutRunning={sigma.isLayoutRunning}
        selectedNodeLabel={selectedLabel}
        onStop={sigma.stopLayout}
        onRestart={sigma.restartLayout}
      />
    </div>
  );
}
